import { z } from "zod";
import { zEmail, zId } from "./common.js";
import { zLanguage, type PublicLanguageInfo } from "./i18n.js";

export const zUpsertVolunteerOpportunity = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(5000).optional(),
  location: z.string().trim().max(300).optional(),
  skills: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
  archived: z.boolean().default(false),
  /** CT-RM-27: requirements / waiver text a self-signup must agree to (unchanged when omitted) */
  requirements: z.string().max(10_000).nullable().optional(),
  /** CT-RM-27: full shifts take a waitlist that auto-promotes in order (unchanged when omitted) */
  waitlistEnabled: z.boolean().optional(),
  /** CT-RM-36: signups are held as pending_screening until the volunteer has a clear background check (unchanged when omitted) */
  requiresBackgroundCheck: z.boolean().optional(),
  /** CT-RM-36: a clear check counts for this many months after it completed; null = until the check itself expires */
  backgroundCheckValidityMonths: z.number().int().min(1).max(120).nullable().optional(),
});
export type UpsertVolunteerOpportunity = z.infer<typeof zUpsertVolunteerOpportunity>;

export const zUpsertVolunteerShift = z
  .object({
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    capacity: z.number().int().min(1).max(10_000).nullable().optional(),
    notes: z.string().max(2000).optional(),
  })
  .refine((s) => new Date(s.endsAt) > new Date(s.startsAt), { message: "Shift must end after it starts" });
export type UpsertVolunteerShift = z.infer<typeof zUpsertVolunteerShift>;

export const zCreateVolunteerSignup = z.object({
  constituentId: zId,
});
export type CreateVolunteerSignup = z.infer<typeof zCreateVolunteerSignup>;

/** staff hour entry (check-in/out computes this automatically) */
export const zLogVolunteerHours = z.object({
  minutes: z.number().int().min(1).max(1440),
});
export type LogVolunteerHours = z.infer<typeof zLogVolunteerHours>;

/**
 * `waitlisted` (CT-RM-27) holds no spot; it is promoted to `signed_up` in join order when one frees up.
 * `pending_screening` (CT-RM-36) holds the spot but can't check in until the volunteer's background
 * check clears (it is released to `signed_up` automatically) or staff override the hold.
 */
export const VOLUNTEER_SIGNUP_STATUSES = ["signed_up", "completed", "cancelled", "no_show", "waitlisted", "pending_screening"] as const;
export const zVolunteerSignupStatus = z.enum(VOLUNTEER_SIGNUP_STATUSES);
export type VolunteerSignupStatus = z.infer<typeof zVolunteerSignupStatus>;
/** statuses that occupy one of a shift's capacity spots */
export const VOLUNTEER_SPOT_STATUSES = ["signed_up", "completed", "no_show", "pending_screening"] as const satisfies readonly VolunteerSignupStatus[];

// ---- CT-RM-27: public board, self-signup, self-service, hour approval ----

/** volunteer-logged hours wait for staff review; staff-logged hours are approved on entry */
export const VOLUNTEER_HOURS_STATUSES = ["pending", "approved", "rejected"] as const;
export type VolunteerHoursStatus = (typeof VOLUNTEER_HOURS_STATUSES)[number];

/** the emailed self-service link works once and expires; it exchanges for a short session */
export const VOLUNTEER_LINK_TTL_HOURS = 72;
export const VOLUNTEER_SESSION_TTL_HOURS = 2;
/** name of the hidden honeypot input on public signup forms — humans leave it empty */
export const VOLUNTEER_HONEYPOT_FIELD = "website";

export const zPublishVolunteerOpportunity = z.object({ published: z.boolean() });
export type PublishVolunteerOpportunity = z.infer<typeof zPublishVolunteerOpportunity>;

export const zPublicVolunteerSignup = z.object({
  /** CT-RM-30: the board language; a new constituent gets it as their preferred language */
  language: zLanguage.optional(),
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: zEmail,
  phone: z.string().trim().max(40).optional(),
  /** must be true when the opportunity lists requirements / waiver text */
  agreed: z.boolean().default(false),
  /** honeypot: any non-empty value marks the post as a bot */
  [VOLUNTEER_HONEYPOT_FIELD]: z.string().max(500).optional(),
});
export type PublicVolunteerSignup = z.input<typeof zPublicVolunteerSignup>;

export interface PublicVolunteerSignupResult {
  status: "signed_up" | "waitlisted" | "pending_screening";
  /** 1-based place in line when waitlisted */
  waitlistPosition: number | null;
}

export interface PublicVolunteerShift {
  id: string;
  startsAt: string;
  endsAt: string;
  notes: string | null;
  capacity: number | null;
  /** null = unlimited */
  remaining: number | null;
  waitlistOpen: boolean;
}

export interface PublicVolunteerOpportunity {
  id: string;
  name: string;
  description: string | null;
  location: string | null;
  skills: string[];
  requirements: string | null;
  waitlistEnabled: boolean;
  /** CT-RM-36: new volunteers are held until a background check clears */
  requiresBackgroundCheck: boolean;
  shifts: PublicVolunteerShift[];
}

export interface PublicVolunteerBoard extends PublicLanguageInfo {
  organizationName: string;
  /** IANA zone shift times are shown in */
  timeZone: string;
  opportunities: PublicVolunteerOpportunity[];
}

export const zVolunteerRequestLink = z.object({ email: zEmail });
export const zVolunteerExchange = z.object({ token: z.string().min(20).max(400) });

export const zVolunteerSelfLogHours = z.object({
  minutes: z.number().int().min(1).max(1440),
  note: z.string().trim().max(1000).optional(),
});
export type VolunteerSelfLogHours = z.infer<typeof zVolunteerSelfLogHours>;

export interface VolunteerSelfShift {
  signupId: string;
  opportunityName: string;
  location: string | null;
  startsAt: string;
  endsAt: string;
  status: VolunteerSignupStatus;
  waitlistPosition: number | null;
  minutes: number | null;
  pendingMinutes: number | null;
  hoursStatus: VolunteerHoursStatus | null;
  canCancel: boolean;
  canLogHours: boolean;
}

export interface VolunteerSelfService {
  organizationName: string;
  timeZone: string;
  displayName: string;
  upcoming: VolunteerSelfShift[];
  past: VolunteerSelfShift[];
}

export const zReviewVolunteerHours = z.object({
  decision: z.enum(["approve", "reject"]),
  /** approve with a corrected amount (defaults to what the volunteer logged) */
  minutes: z.number().int().min(1).max(1440).optional(),
});
export type ReviewVolunteerHours = z.infer<typeof zReviewVolunteerHours>;

export interface VolunteerHoursQueueRow {
  signupId: string;
  constituentId: string;
  constituentName: string;
  opportunityId: string;
  opportunityName: string;
  startsAt: string;
  endsAt: string;
  pendingMinutes: number;
  note: string | null;
  submittedAt: string;
}

export interface VolunteerWaitlistRow {
  signupId: string;
  constituentId: string;
  constituentName: string;
  position: number;
  joinedAt: string;
}

export const zVolunteerSettings = z.object({
  /** queue a confirmation email on self-signup / waitlist / promotion (default off) */
  confirmationEmails: z.boolean(),
  /** queue a reminder email before each shift (default off) */
  reminderEmails: z.boolean(),
  reminderHoursBefore: z.number().int().min(1).max(168),
});
export type VolunteerSettings = z.infer<typeof zVolunteerSettings>;
export const DEFAULT_VOLUNTEER_SETTINGS: VolunteerSettings = { confirmationEmails: false, reminderEmails: false, reminderHoursBefore: 24 };

// ---- CT-RM-36: volunteer background checks (screening) ----
// Records hold workflow state only — provider, reference, dates. No SSNs, dates of
// birth, report contents or other identity data are ever stored or sent.

export const VOLUNTEER_SCREENING_STATUSES = ["not_started", "invited", "pending", "clear", "consider", "expired", "cancelled"] as const;
export const zVolunteerScreeningStatus = z.enum(VOLUNTEER_SCREENING_STATUSES);
export type VolunteerScreeningStatus = z.infer<typeof zVolunteerScreeningStatus>;

/** a constituent has at most one screening in one of these states at a time */
export const VOLUNTEER_SCREENING_OPEN_STATUSES = ["not_started", "invited", "pending"] as const satisfies readonly VolunteerScreeningStatus[];

/** allowed moves; expired and cancelled are final (start a new check instead) */
export const VOLUNTEER_SCREENING_TRANSITIONS: Record<VolunteerScreeningStatus, readonly VolunteerScreeningStatus[]> = {
  not_started: ["invited", "cancelled"],
  invited: ["pending", "clear", "consider", "cancelled"],
  pending: ["clear", "consider", "cancelled"],
  clear: ["expired", "cancelled"],
  consider: ["clear", "cancelled"],
  expired: [],
  cancelled: [],
};
export const canTransitionScreening = (from: VolunteerScreeningStatus, to: VolunteerScreeningStatus) => VOLUNTEER_SCREENING_TRANSITIONS[from].includes(to);

export const VOLUNTEER_SCREENING_STATUS_LABELS: Record<VolunteerScreeningStatus, string> = {
  not_started: "Not started",
  invited: "Invited",
  pending: "In progress",
  clear: "Clear",
  consider: "Consider",
  expired: "Expired",
  cancelled: "Cancelled",
};

/** screening providers. Only the built-in test-mode stub exists: it never makes a network call. */
export const VOLUNTEER_SCREENING_PROVIDERS = ["test"] as const;
export type VolunteerScreeningProvider = (typeof VOLUNTEER_SCREENING_PROVIDERS)[number];

/** how long a clear check stays valid when nothing else says so */
export const DEFAULT_SCREENING_VALIDITY_MONTHS = 24;
/** clear checks expiring within this window get a staff follow-up task */
export const SCREENING_EXPIRY_WARNING_DAYS = 30;

/** "2026-11-04" + n months, clamped to the month's last day */
export function addMonthsIso(isoDate: string, months: number): string {
  const [y, m, d] = isoDate.slice(0, 10).split("-").map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = total % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

/**
 * Does this screening satisfy an opportunity's background-check rule on `onDate` (YYYY-MM-DD)?
 * It must be clear, not past its own expiry, and — when the opportunity sets a validity
 * period — completed within that many months.
 */
export function screeningCovers(
  screening: { status: VolunteerScreeningStatus; completedAt: string | null; expiresAt: string | null } | null | undefined,
  rule: { validityMonths: number | null },
  onDate: string,
): boolean {
  if (!screening || screening.status !== "clear" || !screening.completedAt) return false;
  if (screening.expiresAt && screening.expiresAt <= onDate) return false;
  if (rule.validityMonths != null && addMonthsIso(screening.completedAt, rule.validityMonths) <= onDate) return false;
  return true;
}

/** start a check. Consent is captured in-app (staff confirm it); no consent email is ever sent. */
export const zCreateVolunteerScreening = z.object({
  constituentId: zId,
  provider: z.enum(VOLUNTEER_SCREENING_PROVIDERS).default("test"),
  /** true: the volunteer's consent was captured in-app, so the check is submitted to the provider now (→ invited) */
  consentCaptured: z.boolean().default(false),
});
export type CreateVolunteerScreening = z.input<typeof zCreateVolunteerScreening>;

/** submit a not_started check to its provider once consent is captured in-app */
export const zInitiateVolunteerScreening = z.object({ consentCaptured: z.literal(true) });
export type InitiateVolunteerScreening = z.infer<typeof zInitiateVolunteerScreening>;

/** test mode only: staff pick the result the stub provider reports */
export const zSimulateVolunteerScreening = z.object({
  outcome: z.enum(["pending", "clear", "consider"]),
  /** a clear check's validity; defaults to the longest period among opportunities requiring a check, else 24 */
  validityMonths: z.number().int().min(1).max(120).optional(),
});
export type SimulateVolunteerScreening = z.infer<typeof zSimulateVolunteerScreening>;

/** staff release a signup held for screening anyway; the reason is audited */
export const zOverrideVolunteerScreening = z.object({ reason: z.string().trim().min(3).max(500) });
export type OverrideVolunteerScreening = z.infer<typeof zOverrideVolunteerScreening>;

export const zVolunteerScreeningReportQuery = z.object({
  /** latest-check status; not_started includes volunteers with no check on file */
  status: zVolunteerScreeningStatus.optional(),
  /** expiring: clear checks expiring within SCREENING_EXPIRY_WARNING_DAYS; held: volunteers with signups held for screening */
  attention: z.enum(["expiring", "held"]).optional(),
});
export type VolunteerScreeningReportQuery = z.infer<typeof zVolunteerScreeningReportQuery>;

export interface VolunteerScreening {
  id: string;
  constituentId: string;
  status: VolunteerScreeningStatus;
  provider: VolunteerScreeningProvider;
  /** the provider's reference for the check (never identity data) */
  reference: string | null;
  testMode: boolean;
  consentCapturedAt: string | null;
  invitedAt: string | null;
  completedAt: string | null;
  /** YYYY-MM-DD; a clear check stops counting on this date */
  expiresAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VolunteerHeldSignup {
  signupId: string;
  opportunityId: string;
  opportunityName: string;
  startsAt: string;
}

/** constituent view: the current check, its history, and any signups waiting on it */
export interface ConstituentScreening {
  current: VolunteerScreening | null;
  history: VolunteerScreening[];
  heldSignups: VolunteerHeldSignup[];
  /** the constituent has signed up for an opportunity that requires a check */
  requiresCheck: boolean;
}

export interface VolunteerScreeningReportRow {
  constituentId: string;
  constituentName: string;
  /** the latest check's status, or not_started when none is on file */
  status: VolunteerScreeningStatus;
  screening: VolunteerScreening | null;
  heldSignups: number;
  upcomingShifts: number;
  requiresCheck: boolean;
  expiringSoon: boolean;
}

export interface VolunteerScreeningReport {
  rows: VolunteerScreeningReportRow[];
  /** across all volunteers, before filters */
  counts: Record<VolunteerScreeningStatus, number>;
  expiringSoon: number;
  heldSignups: number;
}

export interface VolunteerScreeningSweepResult {
  expired: number;
  flagged: number;
  tasks: number;
}
